import { html, TemplateResult, SVGTemplateResult } from 'lit';
import componentStyles from './idb-special-icon-card.css.lit';
import '../idb-icon/idb-icon';
import '../idb-button/idb-button';
import { customElement, property } from 'lit/decorators.js';
import customIcons from './custom-icons';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeSVG } from 'lit-html/directives/unsafe-svg.js';
import { LinkTargetType } from '../../base/outline-link/config';
import { OutlineElement } from 'src/components/base/outline-element/outline-element';
import { HeadingLevelType } from 'src/components/base/outline-heading/config';
import '../idb-heading/idb-heading';

const iconMap = {
  courses: customIcons.courses,
  publications: customIcons.publications,
  numbers: customIcons.numbers,
  blogs: customIcons.blogs,
  events: customIcons.events,
  projects: customIcons.projects,
  benefits: customIcons.benefits,
  proposals: customIcons.proposals,
  jobs: customIcons.jobs,
};

export type CustomIcon = keyof typeof iconMap;
export type BackgroundColors = 'white' | 'gray';
export const customIconArr = Object.keys(iconMap) as CustomIcon[];

@customElement('idb-special-icon-card')
export class SpecialIconCard extends OutlineElement {
  static styles = [componentStyles];

  @property({ type: String, reflect: true, attribute: 'url' })
  url: string;
  /**
   * The color class to apply to the full card.
   */
  @property({ attribute: 'background-color' })
  backgroundColor: BackgroundColors;

  /**
   * The aria label to be passed to the cta link
   */
  @property({ type: String, attribute: 'button-label' })
  buttonLabel = 'Link Button';

  /**
   * The target for the button. Defaulting to _blank to prevent rework from Integration team.
   */
  @property({ type: String, attribute: 'button-target' })
  buttonTarget: LinkTargetType = '_blank';

  /**
   * The heading tag to apply: p | span | h1 | h2 | h3 | h4 | h5 | h6
   */
  @property({ type: String, reflect: true, attribute: 'level' })
  level: HeadingLevelType = 'h3';

  /**
   * The icon to display in the card.
   */
  @property({ type: String, attribute: 'icon' })
  icon: keyof typeof iconMap;

  render(): TemplateResult {
    const cardClasses = {
      [`idb-special-icon-card-bg-${this.backgroundColor}`]:
        this.backgroundColor !== undefined,
      'idb-special-icon-card': true,
    };
    return html`
            <section class="${classMap(cardClasses)}">
                <div class="header">
                  <div class="icon" aria-hidden="true">
                    ${unsafeSVG(iconMap[this.icon]) as SVGTemplateResult}
                  </div>
                    <div class="titles">
                        <idb-heading
                          level="${this.level}"
                          level-size="h3-medium"
                          level-style="bold"
                        >
                          ${this._conditionalSlotTemplate({
                            slotName: 'heading',
                          })}
                        </idb-heading>
                        ${this._conditionalSlotTemplate({
                          slotName: 'subtitle',
                        })}
                    </div>
                </div>
                ${
                  this.url
                    ? html`
                <div class="footer">
                    <idb-button
                      class="mr-2"
                      button-size="small"
                      button-url="${this.url}"
                      icon-right="arrow-right"
                      button-variant="secondary"
                      button-target="${this.buttonTarget}"
                      button-label="${this.buttonLabel}"
                      aria-disabled="false"
                    >
                        ${this._conditionalSlotTemplate({
                          slotName: 'cta-button',
                        })}
                    </idb-button>
                </div>
                `
                    : ''
                }
            </section>
        `;
  }
}
